import { VendorQuote } from "./models/VendorQuote";
import { Fee } from "./models/Fee";
import { FeeType, isRcsFeeType } from "./models/FeeType";
import { FixedPriceType, normalizeFixedPriceType } from "./models/FixedPriceType";
import { DiscountType } from "./models/DiscountType";

/**
 * Legacy stored rows that are now computed (TAX on sell, CC per row).
 * They never count toward cost or sell.
 */
export const isLegacyComputedFee = (f: Fee): boolean =>
  f.feeType === "TAX" || f.feeType === FeeType.RCS_CREDIT_CARD;

export const isVendorFee = (f: Fee): boolean =>
  f.vendorFee != null && !isLegacyComputedFee(f);

/** Customer-facing fee lines (excludes vendor-only: vendorFee set and customerFee === 0). */
export const isCustomerFee = (f: Fee): boolean =>
  !isLegacyComputedFee(f) && (isVendorFee(f) || f.customerFee !== null);

const getVendorFees = (quote: VendorQuote): Fee[] =>
  (quote.fees ?? []).filter(isVendorFee);

export const getCustomerFees = (quote: VendorQuote): Fee[] =>
  (quote.fees ?? []).filter(isCustomerFee);

export const getVendorFeeAmount = (fee: Fee) => {
  return fee.vendorFee ?? 0;
};

/**
 * Fee Cost Each / Sell on the pricing worksheet: break fee-unit amount across items.
 * amount / (itemQty / feeQty) === (amount * feeQty) / itemQty
 */
export const getFeeAmountPerItem = (
  feeUnitAmount: number,
  feeQty: number,
  itemQty: number
): number => {
  const safeFeeQty = feeQty > 0 ? feeQty : 1;
  const safeItemQty = itemQty > 0 ? itemQty : 1;
  return (feeUnitAmount * safeFeeQty) / safeItemQty;
};

/** Inverse of getFeeAmountPerItem — restore stored fee-unit amount from a per-item entry. */
export const getFeeUnitAmountFromPerItem = (
  perItemAmount: number,
  feeQty: number,
  itemQty: number
): number => {
  const safeFeeQty = feeQty > 0 ? feeQty : 1;
  const safeItemQty = itemQty > 0 ? itemQty : 1;
  return (perItemAmount * safeItemQty) / safeFeeQty;
};

export const MAX_DECIMAL_PLACES = 6;

export enum PriceAdjustmentType {
  ROUND_UP = "round_up",
  ROUND_DOWN = "round_down",
  ROUND = "round",
}

export const getCustomerFeeAmount = (fee: Fee): number => {
  if (!fee.isPercentMarkup) {
    return fee.customerFee ?? 0;
  }
  return getVendorFeeAmount(fee) * (1 + (fee.customerFee ?? 0) / 100);
};

export const getTotalVendorFees = (vendorQuote: VendorQuote) => {
  const vendorFees = getVendorFees(vendorQuote);
  return vendorFees.reduce((acc, fee) => {
    return acc + getVendorFeeAmount(fee) * fee.qty;
  }, 0);
};

/** Dollar total of the vendor discount line (0 when unset). */
export const getVendorDiscountTotal = (vendorQuote: VendorQuote): number => {
  const qty = vendorQuote.vendorQty ?? vendorQuote.discountQty ?? 0;
  const amount = vendorQuote.discountAmount ?? 0;
  if (!qty && vendorQuote.discountType !== DiscountType.TOTAL) {
    return 0;
  }
  switch (vendorQuote.discountType) {
    case DiscountType.TOTAL:
      return Math.max(0, amount);
    case DiscountType.PERCENT:
    case DiscountType.PERCENT_TOTAL:
      return Math.max(
        0,
        vendorQuote.pricePerVendorUnit * qty * (amount / 100)
      );
    case DiscountType.EACH:
    default:
      return Math.max(0, amount * qty);
  }
};

/** Per-unit vendor discount for the discount qty (0 when unset / total mode with qty 0). */
export const getVendorDiscountEach = (vendorQuote: VendorQuote): number => {
  const qty = vendorQuote.vendorQty ?? vendorQuote.discountQty ?? 0;
  const total = getVendorDiscountTotal(vendorQuote);
  if (qty <= 0) {
    return total;
  }
  return total / qty;
};

export const roundToDecimalPlaces = (
  value: number,
  decimalPlaces: number
): number => {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
};

/** Round toward +∞ to `decimalPlaces` (e.g. 1.231 → 1.24 at 2 places). */
export const ceilToDecimalPlaces = (
  value: number,
  decimalPlaces: number
): number => {
  if (!Number.isFinite(value)) {
    return value;
  }
  const factor = 10 ** decimalPlaces;
  // Subtract a tiny epsilon so exact 2-dp values are not nudged up by float noise.
  return Math.ceil(value * factor - 1e-9) / factor;
};

/**
 * How many fraction digits to show: at least `minPlaces`, at most `maxPlaces`,
 * with trailing zeros stripped (131.2500 → 2 places, 175 → 2 places).
 */
export const decimalPlacesNeeded = (
  value: number,
  maxPlaces: number,
  minPlaces = 2
): number => {
  if (!Number.isFinite(value)) {
    return minPlaces;
  }
  const max = Math.max(0, Math.min(MAX_DECIMAL_PLACES, maxPlaces));
  const min = Math.max(0, Math.min(minPlaces, max));
  const fixed = roundToDecimalPlaces(value, max).toFixed(max);
  const frac = fixed.split(".")[1] ?? "";
  let needed = frac.length;
  while (needed > min && frac[needed - 1] === "0") {
    needed -= 1;
  }
  return needed;
};

/** Money display string (no $ / commas): trim trailing zeros, keep ≥2 decimals. */
export const formatMoneyDecimals = (
  value: number,
  maxPlaces: number,
  minPlaces = 2
): string => {
  if (!Number.isFinite(value)) {
    return "";
  }
  const places = decimalPlacesNeeded(value, maxPlaces, minPlaces);
  return roundToDecimalPlaces(value, maxPlaces).toFixed(places);
};

/** Display markup / margin % — up to MAX_DECIMAL_PLACES, trailing zeros trimmed. */
export const formatMarkupPercentValue = (markupPercent: number): string => {
  if (!Number.isFinite(markupPercent)) {
    return markupPercent === Infinity || markupPercent === -Infinity
      ? "∞"
      : "";
  }
  return formatMoneyDecimals(markupPercent, MAX_DECIMAL_PLACES, 0);
};

export const truncateToDecimalPlaces = (
  value: number,
  decimalPlaces: number
): number => {
  const factor = 10 ** decimalPlaces;
  return Math.floor(value * factor) / factor;
};

/**
 * Excel Bid-3 "Included" fees (Inc. checked → isHidden): vendor $ summed into
 * Shipping Total (C27), then rolled into Cost Each. Fee markup % is unused here.
 */
export const getIncludedVendorFeesTotal = (vendorQuote: VendorQuote): number => {
  return (vendorQuote.fees ?? []).reduce((acc, fee) => {
    if (!fee.isHidden || isLegacyComputedFee(fee)) {
      return acc;
    }
    return acc + getVendorFeeAmount(fee) * fee.qty;
  }, 0);
};

/** Excel Not Included fee vendor $ (itemized; not in Cost Each). */
export const getNotIncludedVendorFeesTotal = (
  vendorQuote: VendorQuote
): number => {
  return getVendorFees(vendorQuote).reduce((acc, fee) => {
    if (fee.isHidden) {
      return acc;
    }
    return acc + getVendorFeeAmount(fee) * fee.qty;
  }, 0);
};

/** Excel C28: Shipping Cost Each = C27 / Qty. */
export const getShippingCostEach = (vendorQuote: VendorQuote): number => {
  const qty = vendorQuote.vendorQty;
  const included = getIncludedVendorFeesTotal(vendorQuote);
  return qty > 0 ? included / qty : 0;
};

/** Excel C29: Cost Each = Unit Cost + Shipping Cost Each. */
export const getCostEach = (vendorQuote: VendorQuote): number => {
  return vendorQuote.pricePerVendorUnit + getShippingCostEach(vendorQuote);
};

/** Included RCS Credit Card vendor $ — legacy fee rows; always 0 after redesign. */
export const getIncludedRcsCreditCardVendorTotal = (
  _vendorQuote: VendorQuote
): number => {
  return 0;
};

/** Included RCS Credit Card customer $ — legacy; always 0 after redesign. */
export const getIncludedRcsCreditCardCustomerTotal = (
  _vendorQuote: VendorQuote
): number => {
  return 0;
};

/**
 * Cost Each used for unit-sell markup.
 * (Legacy RCS CC fee exclusion no longer needed — CC is a client-only column.)
 */
export const getCostEachForMarkup = (vendorQuote: VendorQuote): number => {
  return getCostEach(vendorQuote);
};

export const DEFAULT_CREDIT_CARD_RATE_PERCENT = 3;

export const RCS_ADMIN_FEE_TYPE = "RCS_ADMIN_FEE";
/** % of each credit card line's sell + tax. */
export const DEFAULT_RCS_ADMIN_FEE_RATE_PERCENT = 1.35;
/** Charged once per credit card line. */
export const DEFAULT_RCS_ADMIN_FEE_FLAT_AMOUNT = 3.18;

let rcsAdminFeeRatePercent = DEFAULT_RCS_ADMIN_FEE_RATE_PERCENT;
let rcsAdminFeeFlatAmount = DEFAULT_RCS_ADMIN_FEE_FLAT_AMOUNT;

/** App-wide RCS Admin Fee (catalog RCS_ADMIN_FEE type). Set when catalog types load. */
export const setRcsAdminFee = (
  ratePercent: number | null | undefined,
  flatAmount: number | null | undefined
) => {
  rcsAdminFeeRatePercent =
    ratePercent != null && Number.isFinite(ratePercent) && ratePercent >= 0
      ? ratePercent
      : DEFAULT_RCS_ADMIN_FEE_RATE_PERCENT;
  rcsAdminFeeFlatAmount =
    flatAmount != null && Number.isFinite(flatAmount) && flatAmount >= 0
      ? flatAmount
      : DEFAULT_RCS_ADMIN_FEE_FLAT_AMOUNT;
};

export const getRcsAdminFeeRates = (): {
  ratePercent: number;
  flatAmount: number;
} => ({
  ratePercent: rcsAdminFeeRatePercent,
  flatAmount: rcsAdminFeeFlatAmount,
});

export interface RcsAdminFee {
  ratePercent: number;
  /** Flat $ added once per line item that has credit card applied. */
  flatAmount: number;
  /** Number of line items charged the flat $. */
  lineCount: number;
  amount: number;
}

export interface QuoteLevelTotals {
  subtotal: number;
  tax: number;
  cc: number;
  /** Subtotal + tax + CC. */
  totalBeforeAdminFee: number;
  /** Sell + tax of the credit card lines (the admin fee % base). */
  adminFeeBase: number;
  /** Σ per-line admin fees; null unless some line has credit card applied. */
  adminFee: RcsAdminFee | null;
  total: number;
}

/**
 * Whole-quote totals across the selected vendor quotes (one per line). The
 * RCS Admin Fee flat $ is charged once per credit card line item (see
 * {@link getQuotePriceBreakdown}); this sums it.
 */
export const getQuoteLevelTotals = (
  vendorQuotes: VendorQuote[],
  creditCardRatePercent?: number | null
): QuoteLevelTotals => {
  let subtotal = 0;
  let tax = 0;
  let cc = 0;
  let admin = 0;
  let adminFeeBase = 0;
  let adminLineCount = 0;
  for (const vq of vendorQuotes) {
    const breakdown = getQuotePriceBreakdown(vq, {
      creditCardRatePercent: creditCardRatePercent ?? undefined,
    });
    subtotal += breakdown.subtotal;
    tax += breakdown.taxTotal;
    cc += breakdown.ccTotal;
    admin += breakdown.adminTotal;
    adminFeeBase += breakdown.adminFeeBase;
    adminLineCount += breakdown.adminFeeLineCount;
  }
  const totalBeforeAdminFee = subtotal + tax + cc;
  const adminFee: RcsAdminFee | null =
    adminLineCount > 0
      ? {
          ...getRcsAdminFeeRates(),
          lineCount: adminLineCount,
          amount: admin,
        }
      : null;
  return {
    subtotal,
    tax,
    cc,
    totalBeforeAdminFee,
    adminFeeBase,
    adminFee,
    total: ceilToDecimalPlaces(totalBeforeAdminFee + admin, 2),
  };
};

/**
 * Customer fee sells for the credit-card / client-before-CC base.
 * Included and Not Included fees both count (each has its own M/U without CC).
 */
export const getFeeSellsForClientBase = (
  vendorQuote: VendorQuote,
  includeItemizedFees = true,
  includeHiddenFees = true
): number => {
  return (vendorQuote.fees ?? []).reduce((acc, fee) => {
    if (isLegacyComputedFee(fee)) {
      return acc;
    }
    if (!includeItemizedFees && !fee.isHidden) {
      return acc;
    }
    if (!includeHiddenFees && fee.isHidden) {
      return acc;
    }
    if (fee.vendorFee == null && fee.customerFee == null) {
      return acc;
    }
    return acc + getCustomerFeeAmount(fee) * fee.qty;
  }, 0);
};

/** @deprecated Use getFeeSellsForClientBase — kept for call-site compatibility. */
export const getNotIncludedCustomerFeesForCreditCardBase = (
  vendorQuote: VendorQuote
): number => {
  return getFeeSellsForClientBase(vendorQuote, true, false);
};

/**
 * Client sell before credit card: merchandise M/U without CC (unit-cost markup)
 * + every fee's M/U without CC (Included and Not Included).
 * Example: $48 line + $10 included Environmental = $58.
 */
export const getClientSellBeforeCreditCard = (
  vendorQuote: VendorQuote,
  includeItemizedFees = true,
  includeHiddenFees = true
): number => {
  return getQuotePriceBreakdown(
    vendorQuote,
    {},
    includeItemizedFees,
    includeHiddenFees
  ).subtotal;
};

export const DEFAULT_TAX_RATE_PERCENT = 7;

let defaultTaxRatePercent = DEFAULT_TAX_RATE_PERCENT;

/** App-wide tax rate (catalog TAX type). Set once when catalog types load. */
export const setDefaultTaxRatePercent = (ratePercent: number | null | undefined) => {
  defaultTaxRatePercent =
    ratePercent != null && Number.isFinite(ratePercent) && ratePercent >= 0
      ? ratePercent
      : DEFAULT_TAX_RATE_PERCENT;
};

export const getDefaultTaxRatePercent = (): number => defaultTaxRatePercent;

export interface PricingRates {
  /** RFQ credit card rate % (defaults to DEFAULT_CREDIT_CARD_RATE_PERCENT). */
  creditCardRatePercent?: number | null;
  /** Sales tax rate % (defaults to the catalog TAX rate). */
  taxRatePercent?: number | null;
  /** RCS admin fee % per credit card line (defaults to the catalog rate). */
  adminFeeRatePercent?: number | null;
  /** RCS admin fee flat $ per credit card line item (defaults to the catalog amount). */
  adminFeeFlatAmount?: number | null;
}

const resolveRate = (rate: number | null | undefined, fallback: number) =>
  rate != null && Number.isFinite(rate) && rate >= 0 ? rate : fallback;

export const resolvePricingRates = (
  rates: PricingRates = {}
): {
  creditCardRatePercent: number;
  taxRatePercent: number;
  adminFeeRatePercent: number;
  adminFeeFlatAmount: number;
} => ({
  creditCardRatePercent: resolveRate(
    rates.creditCardRatePercent,
    DEFAULT_CREDIT_CARD_RATE_PERCENT
  ),
  taxRatePercent: resolveRate(rates.taxRatePercent, defaultTaxRatePercent),
  adminFeeRatePercent: resolveRate(
    rates.adminFeeRatePercent,
    rcsAdminFeeRatePercent
  ),
  adminFeeFlatAmount: resolveRate(
    rates.adminFeeFlatAmount,
    rcsAdminFeeFlatAmount
  ),
});

export interface RowPrice {
  /** M/U without CC: row sell before tax, credit card, and admin fee. */
  sell: number;
  tax: number;
  cc: number;
  /** RCS admin fee (credit card lines only). */
  admin: number;
}

export interface QuotePriceBreakdown {
  /** Merchandise row (base line minus Included fee rows). */
  base: RowPrice;
  feesById: Record<number, RowPrice>;
  /** Client Quote base line, per unit: merchandise + Included fee sells. */
  baseLineEach: RowPrice;
  /** baseLineEach × qty (merchandise + Included fees). */
  baseLine: RowPrice;
  /** Per-unit (per fee qty) amounts for Not Included fee rows. */
  feesEachById: Record<number, RowPrice>;
  subtotal: number;
  taxTotal: number;
  ccTotal: number;
  adminTotal: number;
  /** Sell + tax of the credit card lines (the admin fee % base). */
  adminFeeBase: number;
  /** 1 when any client line has credit card (flat $ is charged once), else 0. */
  adminFeeLineCount: number;
  /** total ÷ qty (unrounded). */
  totalEach: number;
  /** Subtotal + tax + CC + admin, rounded up to the next cent at the end. */
  total: number;
  taxRatePercent: number;
  creditCardRatePercent: number;
  adminFeeRatePercent: number;
  adminFeeFlatAmount: number;
}

/**
 * Included fees are part of the base line, so they follow the base row's CC
 * box. Not Included fees are their own client line and use their own CC box.
 */
export const feeHasCreditCard = (vendorQuote: VendorQuote, fee: Fee): boolean =>
  fee.isHidden ? Boolean(vendorQuote.isCreditCard) : Boolean(fee.isCreditCard);

/**
 * Admin fee applies to a credit-card line when its Admin chip is on.
 * A missing chip follows the CC chip so older quotes keep today's totals.
 */
export const chargesAdminFee = (
  creditCard: boolean,
  isAdminFee: boolean | null | undefined
): boolean => Boolean(creditCard) && (isAdminFee == null || Boolean(isAdminFee));

/** Included fees follow the base row's Admin chip; Not Included fees use their own. */
export const feeHasAdminFee = (vendorQuote: VendorQuote, fee: Fee): boolean =>
  chargesAdminFee(
    feeHasCreditCard(vendorQuote, fee),
    fee.isHidden ? vendorQuote.isAdminFee : fee.isAdminFee
  );

/** Fee rows that carry sell (skips legacy computed rows and empty drafts). */
export const isPricedFee = (fee: Fee): boolean =>
  !isLegacyComputedFee(fee) &&
  !(fee.vendorFee == null && fee.customerFee == null);

// Nudge before rounding so float noise (9.85 / 10 = 0.98499…) rounds half-up.
export const roundCentsHalfUp = (value: number) =>
  Math.round(value * 100 + (value >= 0 ? 1e-7 : -1e-7)) / 100;

/**
 * RCS admin fee for one vendor quote (unrounded):
 *   (Σ sell + tax of credit card lines) × admin% + admin flat $
 * The whole fee goes on the base line when that line is charged the admin fee;
 * otherwise it's split across the admin-fee lines by sell + tax.
 * `chargeBases[0]` is the base line; 0 means that line is not charged.
 */
export const allocateAdminFee = (
  chargeBases: number[],
  adminRate: number,
  adminFlat: number
): number[] => {
  const totalBase = chargeBases.reduce((acc, b) => acc + b, 0);
  if (!(totalBase > 0)) {
    return chargeBases.map(() => 0);
  }
  const total = totalBase * adminRate + adminFlat;
  if (chargeBases[0] > 0) {
    return chargeBases.map((_, i) => (i === 0 ? total : 0));
  }
  return chargeBases.map((b) => (total * b) / totalBase);
};

/** Credit card fee: CC% of the amount before the fee (sell + tax + admin). */
export const creditCardFeeOnTotal = (beforeCc: number, ccRate: number) =>
  beforeCc * ccRate;

/**
 * Per-row pricing. Every amount (sell, tax, credit card, admin fee) is kept
 * unrounded; only the final Total is rounded up to the next cent. Displays
 * round to the cent on their own.
 *
 * Client lines are the base line (Client Quote "Base price" = merchandise +
 * Included fee sells per unit) and each Not Included fee. Tax = sell × tax%
 * on Tax-checked parts.
 *
 * Each client line whose CC box is checked (base row for the base line,
 * the fee's own box for a Not Included fee) gets a credit card fee on its
 * final total. The RCS admin fee is charged on those lines only when the
 * Admin chip is also on (figured once on the whole vendor quote, see
 * {@link allocateAdminFee}):
 *   CC    = (sell + tax + admin) × CC%   (see {@link creditCardFeeOnTotal})
 * Subtotal = Σ sell; Total = Subtotal + Σ tax + Σ CC + Σ admin, rounded.
 *
 * `rounded: false` leaves the final Total unrounded too.
 */
export const getQuotePriceBreakdown = (
  vendorQuote: VendorQuote,
  rates: PricingRates = {},
  includeItemizedFees = true,
  includeHiddenFees = true,
  rounded = true
): QuotePriceBreakdown => {
  const {
    creditCardRatePercent,
    taxRatePercent,
    adminFeeRatePercent,
    adminFeeFlatAmount,
  } = resolvePricingRates(rates);
  const taxRate = taxRatePercent / 100;
  const ccRate = creditCardRatePercent / 100;
  const adminRate = adminFeeRatePercent / 100;
  const qty = vendorQuote.vendorQty > 0 ? vendorQuote.vendorQty : 0;
  const pricedFees = (vendorQuote.fees ?? []).filter(isPricedFee);
  const includedFees =
    includeHiddenFees && qty > 0 ? pricedFees.filter((f) => f.isHidden) : [];
  const standaloneFees = pricedFees.filter((f) =>
    f.isHidden ? includeHiddenFees && qty <= 0 : includeItemizedFees
  );

  const rawTax = (sell: number, taxable?: boolean) =>
    taxable ? sell * taxRate : 0;
  const perUnit = (total: number, units: number) =>
    units > 0 ? total / units : total;

  // Base line per unit: merchandise + Included fee sells ÷ qty.
  const unitSell = getUnitSellPrice(vendorQuote, includeHiddenFees);
  const includedShares = includedFees.map((fee) => ({
    fee,
    sellEach: (getCustomerFeeAmount(fee) * (fee.qty || 0)) / qty,
  }));
  const includedSellEach = includedShares.reduce(
    (acc, s) => acc + s.sellEach,
    0
  );
  const lineSellEach = unitSell + includedSellEach;
  const baseLineSell = lineSellEach * qty;
  const baseTax =
    (rawTax(unitSell, vendorQuote.isTaxable) +
      includedShares.reduce(
        (acc, s) => acc + rawTax(s.sellEach, s.fee.isTaxable),
        0
      )) *
    qty;

  const feeLines = standaloneFees.map((fee) => {
    const sellEach = getCustomerFeeAmount(fee);
    const feeQty = fee.qty || 0;
    const sell = sellEach * feeQty;
    return {
      fee,
      sellEach,
      feeQty,
      sell,
      tax: rawTax(sell, fee.isTaxable),
    };
  });
  const taxTotal = baseTax + feeLines.reduce((acc, l) => acc + l.tax, 0);

  /** Sell + tax when the line is charged; 0 otherwise. */
  const lineChargeBase = (enabled: boolean, sell: number, tax: number) => {
    const chargeBase = sell + tax;
    return enabled && chargeBase > 0 ? chargeBase : 0;
  };
  const ccBases = [
    lineChargeBase(Boolean(vendorQuote.isCreditCard), baseLineSell, baseTax),
    ...feeLines.map((line) =>
      lineChargeBase(
        feeHasCreditCard(vendorQuote, line.fee),
        line.sell,
        line.tax
      )
    ),
  ];
  const adminBases = [
    lineChargeBase(
      chargesAdminFee(
        Boolean(vendorQuote.isCreditCard),
        vendorQuote.isAdminFee
      ),
      baseLineSell,
      baseTax
    ),
    ...feeLines.map((line) =>
      lineChargeBase(
        feeHasAdminFee(vendorQuote, line.fee),
        line.sell,
        line.tax
      )
    ),
  ];
  const adminFeeBase = adminBases.reduce((acc, b) => acc + b, 0);
  const adminFeeLineCount = adminFeeBase > 0 ? 1 : 0;
  const admins = allocateAdminFee(adminBases, adminRate, adminFeeFlatAmount);
  const [baseAdmin, ...feeAdmins] = admins;
  const [baseCc, ...feeCcs] = ccBases.map((chargeBase, i) =>
    chargeBase > 0 ? creditCardFeeOnTotal(chargeBase + admins[i], ccRate) : 0
  );

  const baseLine: RowPrice = {
    sell: baseLineSell,
    tax: baseTax,
    cc: baseCc,
    admin: baseAdmin,
  };
  const baseLineEach: RowPrice = {
    sell: lineSellEach,
    tax: perUnit(baseTax, qty),
    cc: perUnit(baseCc, qty),
    admin: perUnit(baseAdmin, qty),
  };

  // Included fee rows keep their share; merchandise absorbs the rounding.
  const shareOfBaseLine = (sell: number, amount: number) =>
    baseLineSell > 0 ? (amount * sell) / baseLineSell : 0;
  const feesById: Record<number, RowPrice> = {};
  const base: RowPrice = { ...baseLine };
  for (const { fee, sellEach } of includedShares) {
    const sell = sellEach * qty;
    const row: RowPrice = {
      sell,
      tax: rawTax(sellEach, fee.isTaxable) * qty,
      cc: shareOfBaseLine(sell, baseLine.cc),
      admin: shareOfBaseLine(sell, baseLine.admin),
    };
    feesById[fee.id] = row;
    base.sell -= row.sell;
    base.tax -= row.tax;
    base.cc -= row.cc;
    base.admin -= row.admin;
  }

  const feesEachById: Record<number, RowPrice> = {};
  let ccTotal = baseLine.cc;
  let adminTotal = baseLine.admin;
  feeLines.forEach((line, i) => {
    const row: RowPrice = {
      sell: line.sell,
      tax: line.tax,
      cc: feeCcs[i],
      admin: feeAdmins[i],
    };
    feesById[line.fee.id] = row;
    feesEachById[line.fee.id] = {
      sell: line.sellEach,
      tax: perUnit(row.tax, line.feeQty),
      cc: perUnit(row.cc, line.feeQty),
      admin: perUnit(row.admin, line.feeQty),
    };
    ccTotal += row.cc;
    adminTotal += row.admin;
  });
  const subtotal =
    baseLine.sell + feeLines.reduce((acc, l) => acc + l.sell, 0);
  const unroundedTotal = subtotal + taxTotal + ccTotal + adminTotal;
  const total = rounded ? ceilToDecimalPlaces(unroundedTotal, 2) : unroundedTotal;
  const totalEach = perUnit(total, qty);

  return {
    base,
    feesById,
    baseLineEach,
    baseLine,
    feesEachById,
    subtotal,
    taxTotal,
    ccTotal,
    adminTotal,
    adminFeeBase,
    adminFeeLineCount,
    totalEach,
    total,
    taxRatePercent,
    creditCardRatePercent,
    adminFeeRatePercent,
    adminFeeFlatAmount,
  };
};

/** All-in client line amount: sell + tax + CC + admin. */
export const rowAllIn = (row: RowPrice): number =>
  row.sell + row.tax + row.cc + row.admin;

/** Client line amount with credit card fees folded in (before tax). */
export const rowSellWithCreditCardFees = (row: RowPrice): number =>
  row.sell + row.cc + row.admin;

/** True when the base row or any Not Included fee row has its CC box checked. */
export const hasAnyCreditCardRow = (vendorQuote: VendorQuote): boolean =>
  Boolean(vendorQuote.isCreditCard) ||
  (vendorQuote.fees ?? []).some(
    (f) => isPricedFee(f) && !f.isHidden && Boolean(f.isCreditCard)
  );

/** True when any row (base or fee) has its Tax box checked. */
export const hasAnyTaxableRow = (vendorQuote: VendorQuote): boolean =>
  Boolean(vendorQuote.isTaxable) ||
  (vendorQuote.fees ?? []).some((f) => isPricedFee(f) && f.isTaxable);

export interface CreditCardAllocation {
  total: number;
  lineShare: number;
  /** CC $ keyed by fee id (only rows with the CC box checked are non-zero). */
  feeSharesById: Record<number, number>;
  beforeCc: number;
}

/** Credit card $ per row, derived from {@link getQuotePriceBreakdown}. */
export const getCreditCardAllocation = (
  vendorQuote: VendorQuote,
  ratePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT,
  includeItemizedFees = true,
  includeHiddenFees = true,
  taxRatePercent?: number
): CreditCardAllocation => {
  const breakdown = getQuotePriceBreakdown(
    vendorQuote,
    { creditCardRatePercent: ratePercent, taxRatePercent },
    includeItemizedFees,
    includeHiddenFees
  );
  const feeSharesById: Record<number, number> = {};
  for (const [id, row] of Object.entries(breakdown.feesById)) {
    feeSharesById[Number(id)] = row.cc;
  }
  return {
    total: breakdown.ccTotal,
    lineShare: breakdown.base.cc,
    feeSharesById,
    beforeCc: breakdown.subtotal,
  };
};

export const getCreditCardTotal = (
  vendorQuote: VendorQuote,
  ratePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT,
  includeItemizedFees = true,
  includeHiddenFees = true
): number => {
  return getCreditCardAllocation(
    vendorQuote,
    ratePercent,
    includeItemizedFees,
    includeHiddenFees
  ).total;
};

/** Excel H29: Unit Total Cost = Cost Each × Qty (before discount). */
export const getUnitTotalCost = (vendorQuote: VendorQuote): number => {
  return getCostEach(vendorQuote) * vendorQuote.vendorQty;
};

/** Excel H31: Unit Sell Total = Unit Sell Price × Qty. */
export const getUnitSellTotal = (vendorQuote: VendorQuote): number => {
  return getUnitSellPrice(vendorQuote, true) * vendorQuote.vendorQty;
};

/** Excel H30: Total Profit = Unit Sell Total − Unit Total Cost. */
export const getHomeworkTotalProfit = (vendorQuote: VendorQuote): number => {
  return getUnitSellTotal(vendorQuote) - getUnitTotalCost(vendorQuote);
};

/** Not Included customer fee $ (itemized sell; add to H31 for all-in client total). */
export const getNotIncludedCustomerFeesTotal = (
  vendorQuote: VendorQuote
): number => {
  return getCustomerFees(vendorQuote).reduce((acc, fee) => {
    if (fee.isHidden) {
      return acc;
    }
    return acc + getCustomerFeeAmount(fee) * fee.qty;
  }, 0);
};

/** Not Included customer fees (shipping, handling, credit card, RCS, etc.). */
export const getNotIncludedLineItemCustomerFeesTotal = (
  vendorQuote: VendorQuote
): number => {
  return getCustomerFees(vendorQuote).reduce((acc, fee) => {
    if (fee.isHidden) {
      return acc;
    }
    return acc + getCustomerFeeAmount(fee) * fee.qty;
  }, 0);
};

/**
 * Vendor merchandise $ only: unit × qty − discount (excludes all fees).
 * Use for Total Cost columns that pair with a separate Line Item Fees breakout.
 */
export const getVendorMerchandiseTotal = (
  vendorQuote: VendorQuote
): number => {
  const vendorUnitPrice = roundToDecimalPlaces(
    vendorQuote.pricePerVendorUnit,
    vendorQuote.unitPriceSigFig
  );
  return roundToDecimalPlaces(
    vendorUnitPrice * vendorQuote.vendorQty -
      getVendorDiscountTotal(vendorQuote),
    vendorQuote.unitPriceSigFig
  );
};

/**
 * Vendor cost for profit: unit × qty − discount + non-RCS fees, rounded to
 * the cent once. Rounding the unit to cents first drifts a cent off the
 * extension (177.897 × 4 = 711.588 → 711.59). RCS fees pass through.
 */
export const getVendorTotalExcludingRcs = (
  vendorQuote: VendorQuote
): number => {
  const nonRcsVendorFees = (vendorQuote.fees ?? []).reduce((acc, fee) => {
    if (isRcsFeeType(fee.feeType)) {
      return acc;
    }
    return acc + getVendorFeeAmount(fee) * (fee.qty || 0);
  }, 0);
  return roundToDecimalPlaces(
    vendorQuote.pricePerVendorUnit * vendorQuote.vendorQty -
      getVendorDiscountTotal(vendorQuote) +
      nonRcsVendorFees,
    2
  );
};

/**
 * "Line Item Fees" column: Not Included customer fees plus Included
 * vendor fees that would otherwise be buried inside Total Cost / Cost Each.
 */
export const getLineItemFeesTotal = (vendorQuote: VendorQuote): number => {
  return (
    getNotIncludedLineItemCustomerFeesTotal(vendorQuote) +
    getIncludedVendorFeesTotal(vendorQuote)
  );
};

/**
 * Excel N25 / Mark Up: (merchandise sell / unit cost) − 1 (as percent).
 * Included fees have their own M/U without CC and are not in this base.
 */
export const getHomeworkMarginPercent = (vendorQuote: VendorQuote): number => {
  const unitCost = vendorQuote.pricePerVendorUnit;
  const unitSell = getUnitSellPrice(vendorQuote, true);
  if (unitCost === 0) {
    return unitSell === 0 ? 0 : Infinity;
  }
  return (unitSell / unitCost - 1) * 100;
};

export const getSellPercentProfit = (
  vendorQuote: VendorQuote,
  includeItemizedVendorFees: boolean,
  includeItemizedSellFees: boolean,
  includeHiddenFees: boolean
) => {
  // Excel Mark Up / N25 — unit sell vs Cost Each (ignore Not-Inc fee lines).
  if (!includeItemizedSellFees) {
    if (!includeHiddenFees) {
      const unitCost = vendorQuote.pricePerVendorUnit;
      const unitSell = getUnitSellPrice(vendorQuote, false);
      if (unitCost === 0) {
        return unitSell === 0 ? 0 : Infinity;
      }
      return (unitSell / unitCost - 1) * 100;
    }
    return getHomeworkMarginPercent(vendorQuote);
  }

  const vendorTotal = getVendorOrderTotal(
    vendorQuote,
    includeItemizedVendorFees
  );
  const customerTotal = getCustomerOrderTotalBeforeCreditCard(
    vendorQuote,
    includeItemizedSellFees,
    includeHiddenFees,
    undefined
  );

  const dollarProfit = customerTotal - vendorTotal;
  const percentProfit =
    vendorTotal === 0
      ? dollarProfit === 0
        ? 0
        : Infinity
      : (dollarProfit / vendorTotal) * 100;
  return percentProfit;
};

/** QuickBooks markup % fields only store 2 decimal places. */
export const roundMarkupPercentForQuickBooks = (
  markupPercent: number
): number => {
  if (!Number.isFinite(markupPercent)) return 0;
  return roundToDecimalPlaces(markupPercent, 2);
};

/**
 * QB estimate line extension (exact homework sell total at 2 decimals).
 * Includes itemized customer fees (e.g. non-included shipping).
 */
export const qbEstimateLineAmount = (lineTotal: number): number => {
  return roundToDecimalPlaces(lineTotal, 2);
};

/**
 * QB estimate / item unit Rate or PurchaseCost from a line total at 2 decimals.
 */
export const qbLineTotalPerUnit = (lineTotal: number, qty: number): number => {
  const total = roundToDecimalPlaces(lineTotal, 2);
  if (!qty || qty <= 0) return total;
  return roundToDecimalPlaces(total / qty, 2);
};

/**
 * QB estimate line unit Rate at 2 decimals from the homework sell total.
 * Bypasses QB markup % (2-decimal limit) so the estimate matches homework pricing.
 */
export const qbEstimateLineUnitRate = (
  lineTotal: number,
  qty: number
): number => {
  return qbLineTotalPerUnit(lineTotal, qty);
};

export const qbEstimateLineMarkupTotal = (
  sellTotal: number,
  costTotal: number
): number => {
  return roundToDecimalPlaces(sellTotal - costTotal, 2);
};

/** Per homework-unit profit for QB estimate (markup total ÷ qty). */
export const qbEstimateUnitProfit = (
  sellTotal: number,
  costTotal: number,
  qty: number
): number => {
  return qbLineTotalPerUnit(
    qbEstimateLineMarkupTotal(sellTotal, costTotal),
    qty
  );
};

/**
 * Pre-tax amount QuickBooks should bill, matching the Client Quote footer.
 * Half-up (subtotal + admin) and the credit card row, then add them. When the
 * footer Each is a unit price, the total is that Each × qty rather than the
 * unextended row sum. Sales tax stays out — QuickBooks applies its own tax.
 */
export const getCustomerOrderTotalForQuickBooks = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT
): number => {
  return getCustomerOrderTotal(
    vendorQuote,
    true,
    true,
    2,
    creditCardRatePercent,
    { includeTax: false }
  );
};

/**
 * Grand total as shown on the Client Quote. Half-up (subtotal + admin), tax,
 * and the credit card fee separately, then add them, so the figure matches
 * the rows above it to the cent.
 */
export const sumDisplayedGrandTotal = (
  subtotalPlusAdmin: number,
  tax: number,
  creditCard: number,
  roundToCents = true
): number => {
  const cents = (value: number) =>
    roundToCents ? roundCentsHalfUp(value) : value;
  const total = cents(subtotalPlusAdmin) + cents(tax) + cents(creditCard);
  // Adding already-rounded cents can leave binary dust (450.47 → 450.46999…).
  return roundToCents ? roundCentsHalfUp(total) : total;
};

/**
 * Total that the cent-rounded Each extends to: half-up(amount / qty) × qty.
 * Half-up of the row sum can be a cent off that (172.32 × 2 = 344.64, while
 * 334.59 + 10.04 = 344.63).
 */
export const extendRoundedEach = (amount: number, qty: number): number => {
  if (!(qty > 0)) {
    return roundCentsHalfUp(amount);
  }
  return roundCentsHalfUp(roundCentsHalfUp(amount / qty) * qty);
};

/**
 * Grand total Each is one unit price when qty is more than 1 and no Not
 * Included fee is its own client line. The total is then Each × qty.
 */
export const clientQuoteGrandEachIsUnitPrice = (
  vendorQuote: VendorQuote
): boolean => {
  if (!(vendorQuote.vendorQty > 1)) {
    return false;
  }
  return !(vendorQuote.fees ?? []).some(
    (fee) =>
      isPricedFee(fee) && !fee.isHidden && getCustomerFeeAmount(fee) !== 0
  );
};

/** Worksheet grand total: row sum, or Each × qty when that Each is the unit price. */
export const clientGrandTotalFromEach = (
  vendorQuote: VendorQuote,
  summedRows: number
): number => {
  if (!clientQuoteGrandEachIsUnitPrice(vendorQuote)) {
    return summedRows;
  }
  return extendRoundedEach(summedRows, vendorQuote.vendorQty);
};

/**
 * Client Quote Total row (before tax and credit card). With one unit price
 * each, that row is Subtotal and the RCS admin fee as shown, each half-up,
 * then added ($3,145.13 + $81.88 = $3,227.01, not $3,227.004 → $3,227.00).
 */
export const clientQuoteTotalBeforeTaxCc = (
  vendorQuote: VendorQuote,
  breakdown: Pick<QuotePriceBreakdown, "subtotal" | "adminTotal">
): number =>
  clientQuoteGrandEachIsUnitPrice(vendorQuote)
    ? roundCentsHalfUp(breakdown.subtotal) +
      roundCentsHalfUp(breakdown.adminTotal)
    : breakdown.subtotal + breakdown.adminTotal;

/**
 * Client Quote base price each after the admin fee, before tax and credit
 * card. Half-up to the cent, matching that row.
 */
export const getClientQuoteBasePriceEach = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT
): number => {
  const qty = vendorQuote.vendorQty;
  const breakdown = getQuotePriceBreakdown(vendorQuote, {
    creditCardRatePercent,
  });
  const line = breakdown.baseLine.sell + breakdown.baseLine.admin;
  return roundCentsHalfUp(qty > 0 ? line / qty : line);
};

/**
 * QB item SALES PRICE each: Client Quote base price each plus the base line's
 * credit card fee (before tax). Half-up to the cent.
 */
export const getQuickBooksSalesPriceEach = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT
): number => {
  const qty = vendorQuote.vendorQty;
  const { baseLine } = getQuotePriceBreakdown(vendorQuote, {
    creditCardRatePercent,
  });
  const line = baseLine.sell + baseLine.admin + baseLine.cc;
  return roundCentsHalfUp(qty > 0 ? line / qty : line);
};

/**
 * Merchandise-only sell total for QB estimate product lines (includes CC,
 * excludes sales tax). Itemized (Not Included) customer fees are emitted as
 * separate estimate lines.
 */
export const getCustomerMerchandiseTotalForQuickBooks = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT
): number => {
  return getCustomerOrderTotal(
    vendorQuote,
    false,
    true,
    2,
    creditCardRatePercent,
    { includeTax: false }
  );
};

/**
 * Client Quote grand total each — the Each on that row.
 * Half-up of the grand total ÷ qty, so Unit Sell × qty is the grand total
 * ($181.48 × 2 = $362.96). Ceiling the raw unit ($181.49) is a cent off.
 */
export const getCustomerBasePriceEach = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT
): number => {
  const qty = vendorQuote.vendorQty;
  const total = getCustomerOrderTotal(
    vendorQuote,
    true,
    true,
    2,
    creditCardRatePercent
  );
  return roundCentsHalfUp(qty > 0 ? total / qty : total);
};

const toCents = (value: number) =>
  Math.round(value * 100 + (value >= 0 ? 1e-7 : -1e-7));

/**
 * Unit sell (SELL_PRICE) whose Client Quote grand total each
 * ({@link getCustomerBasePriceEach}) lands on `target`. Tax, the RCS admin fee
 * and the credit card fee ride on top of the unit sell, so a target each
 * can't be stored as the unit sell directly. Grand each never falls as unit
 * sell rises: bisect, then take the fewest decimals that land on the cent.
 */
export const unitSellForGrandTotalEach = (
  vendorQuote: VendorQuote,
  target: number,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT
): { unitSell: number; sigFig: number } => {
  const baseSigFig = vendorQuote.unitPriceSigFig ?? 2;
  const targetCents = toCents(target);
  if (!(targetCents > 0)) {
    return { unitSell: 0, sigFig: baseSigFig };
  }
  const grandEachCents = (unitSell: number, places: number) =>
    toCents(
      getCustomerBasePriceEach(
        {
          ...vendorQuote,
          fixedPriceType: FixedPriceType.SELL_PRICE,
          fixedPrice: unitSell,
          unitPriceSigFig: Math.max(baseSigFig, places),
        },
        creditCardRatePercent
      )
    );

  let lo = 0;
  let hi = Math.max(target, 1);
  for (
    let guard = 0;
    grandEachCents(hi, MAX_DECIMAL_PLACES) < targetCents && guard < 24;
    guard++
  ) {
    hi = hi * 2 + 1;
  }
  for (let i = 0; i < 48; i++) {
    const mid = (lo + hi) / 2;
    if (grandEachCents(mid, MAX_DECIMAL_PLACES) < targetCents) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  for (let places = 2; places <= MAX_DECIMAL_PLACES; places++) {
    const factor = 10 ** places;
    const center = Math.round(hi * factor);
    for (let step = 0; step <= 8; step++) {
      for (const offset of step === 0 ? [0] : [-step, step]) {
        const unitSell = Math.max(0, (center + offset) / factor);
        if (grandEachCents(unitSell, places) === targetCents) {
          return { unitSell, sigFig: Math.max(baseSigFig, places) };
        }
      }
    }
  }
  return {
    unitSell: Number(hi.toFixed(MAX_DECIMAL_PLACES)),
    sigFig: MAX_DECIMAL_PLACES,
  };
};

/**
 * Base merchandise sell (Excel-ish unit path without fee rollup).
 * Full Unit Sell Price is {@link getUnitSellPrice}.
 */
export const getUnitMarkupPrice = (vendorQuote: VendorQuote) => {
  switch (normalizeFixedPriceType(vendorQuote.fixedPriceType)) {
    case FixedPriceType.SELL_PERCENT_PROFIT:
      return (
        vendorQuote.pricePerVendorUnit * (1 + vendorQuote.fixedPrice / 100)
      );
    case FixedPriceType.VENDOR_MARKUP_PRICE:
      return vendorQuote.fixedPrice;
    case FixedPriceType.SELL_PRICE: {
      // Reverse XL: UnitSell = CostEach × (1+m) → base sell = UnitCost × UnitSell/CostEach
      const costEach = getCostEach(vendorQuote);
      const unitCost = vendorQuote.pricePerVendorUnit;
      if (costEach > 0 && unitCost !== 0) {
        return unitCost * (vendorQuote.fixedPrice / costEach);
      }
      return vendorQuote.fixedPrice;
    }
    default:
      return 0;
  }
};

export const getMarkupPercentage = (vendorQuote: VendorQuote) => {
  return normalizeFixedPriceType(vendorQuote.fixedPriceType) ===
    FixedPriceType.SELL_PERCENT_PROFIT
    ? roundToDecimalPlaces(vendorQuote.fixedPrice, MAX_DECIMAL_PLACES)
    : roundToDecimalPlaces(
        getHomeworkMarginPercent(vendorQuote),
        MAX_DECIMAL_PLACES
      );
};

/**
 * Merchandise unit sell (M/U without CC on the base row).
 * Markup applies to **unit cost only** — Included fees keep their own sell and
 * are added in {@link getClientSellBeforeCreditCard} (e.g. $48 + $10 = $58).
 * `includeHiddenFees` is kept for call-site compatibility (no longer scales sell).
 */
export const getUnitSellPrice = (
  vendorQuote: VendorQuote,
  _includeHiddenFees: boolean
) => {
  const sigFig = vendorQuote.unitPriceSigFig;
  const unitCost = vendorQuote.pricePerVendorUnit;
  const fixedPriceType = normalizeFixedPriceType(vendorQuote.fixedPriceType);

  switch (fixedPriceType) {
    case FixedPriceType.SELL_PERCENT_PROFIT:
      return roundToDecimalPlaces(
        unitCost * (1 + vendorQuote.fixedPrice / 100),
        sigFig
      );
    case FixedPriceType.VENDOR_MARKUP_PRICE:
      return roundToDecimalPlaces(vendorQuote.fixedPrice, sigFig);
    case FixedPriceType.SELL_PRICE:
      return vendorQuote.fixedPrice;
    default:
      return 0;
  }
};

/**
 * Vendor $ total.
 * Always includes Included (hidden) fees — Excel Unit Total Cost / H29 path.
 * includeItemizedFees adds Not Included fee costs (full vendor shipping total).
 */
export const getVendorOrderTotal = (
  vendorQuote: VendorQuote,
  includeItemizedFees: boolean
) => {
  // Round unit first so Totals match displayed Each × Qty (same as customer totals).
  const vendorUnitPrice = roundToDecimalPlaces(
    vendorQuote.pricePerVendorUnit,
    vendorQuote.unitPriceSigFig
  );

  const cost = vendorUnitPrice * vendorQuote.vendorQty;
  const discount = getVendorDiscountTotal(vendorQuote);
  const includedFees = getIncludedVendorFeesTotal(vendorQuote);
  const itemizedFees = includeItemizedFees
    ? getNotIncludedVendorFeesTotal(vendorQuote)
    : 0;

  return roundToDecimalPlaces(
    cost - discount + includedFees + itemizedFees,
    vendorQuote.unitPriceSigFig
  );
};

/** Vendor fees rolled into QB item cost; itemized shipping is excluded. */
export const getVendorFeesInQuickBooksCost = (
  vendorQuote: VendorQuote
): number => {
  return getVendorFees(vendorQuote).reduce((acc, fee) => {
    if (!fee.isHidden && fee.feeType === FeeType.SHIPPING) {
      return acc;
    }
    return acc + getVendorFeeAmount(fee) * fee.qty;
  }, 0);
};

/** QB Item PurchaseCost and COST: custom field — base cost plus included fees only. */
export const getVendorOrderTotalForQuickBooks = (
  vendorQuote: VendorQuote
): number => {
  const cost = vendorQuote.pricePerVendorUnit * vendorQuote.vendorQty;
  const discount = getVendorDiscountTotal(vendorQuote);
  return roundToDecimalPlaces(
    cost - discount + getVendorFeesInQuickBooksCost(vendorQuote),
    2
  );
};

/** Vendor shipping fee for QB SHIPPING FEE: custom field (itemized / not included only). */
export const vendorQuoteShippingFeeTotal = (
  vendorQuote: VendorQuote
): number => {
  const shipping = vendorQuote.fees?.find(
    (f) => f.feeType === FeeType.SHIPPING
  );
  if (!shipping || shipping.isHidden) return 0;
  return roundToDecimalPlaces(getVendorFeeAmount(shipping) * shipping.qty, 2);
};

/** All vendor fees on the quote (Included and Not Included) for QB SHIPPING FEE: custom field. */
export const vendorQuoteFeesTotal = (vendorQuote: VendorQuote): number => {
  const total = getVendorFees(vendorQuote).reduce(
    (acc, fee) => acc + getVendorFeeAmount(fee) * (fee.qty || 0),
    0
  );
  return roundToDecimalPlaces(total, 2);
};

/** PO/Bill vendor line total: QB item cost + itemized shipping fee. */
export const getVendorPurchaseTotalForQuickBooks = (
  costTotal: number,
  shippingFee = 0
): number => {
  return roundToDecimalPlaces(costTotal + shippingFee, 2);
};

/** Per-unit PO/Bill Rate from line cost + shipping. */
export const qbPurchaseOrderUnitCost = (
  costTotal: number,
  shippingFee: number,
  qty: number
): number => {
  return qbLineTotalPerUnit(
    getVendorPurchaseTotalForQuickBooks(costTotal, shippingFee),
    qty
  );
};

/**
 * Decimal places to round the return value.
 * Omitted or 2 → the summary grand total (each row half-up, then added).
 * Explicit `undefined` → no rounding.
 *
 * When producing a rounded money total, unit sell is rounded to
 * `unitPriceSigFig` before × qty so Totals match Each × Qty (avoids
 * 1¢ drift when included fees make the raw unit a repeating decimal).
 *
 * Adds sales tax on Tax-checked rows and credit card + admin fee on CC lines
 * (see {@link getQuotePriceBreakdown}). Pass `creditCardRatePercent` from the
 * RFQ (defaults to 3). `options.includeTax: false` leaves tax out (QuickBooks
 * computes its own sales tax).
 */
export function getCustomerOrderTotal(
  vendorQuote: VendorQuote,
  includeItemizedFees = true,
  includeHiddenFees = true,
  sigfig?: number,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT,
  options: { taxRatePercent?: number | null; includeTax?: boolean } = {}
): number {
  const breakdown = getQuotePriceBreakdown(
    vendorQuote,
    { creditCardRatePercent, taxRatePercent: options.taxRatePercent },
    includeItemizedFees,
    includeHiddenFees
  );
  const tax = options.includeTax === false ? 0 : breakdown.taxTotal;
  const total =
    breakdown.subtotal + breakdown.ccTotal + breakdown.adminTotal + tax;

  // Default param would collapse omitted and explicit undefined; distinguish via arity.
  // Same cents as the summary grand total: half-up each row, then add.
  // Ceiling the raw sum (915.104 → 915.11) drifts a cent off 878.50 + 36.60.
  if (arguments.length < 4 || sigfig === 2) {
    return clientGrandTotalFromEach(
      vendorQuote,
      sumDisplayedGrandTotal(
        clientQuoteTotalBeforeTaxCc(vendorQuote, breakdown),
        tax,
        breakdown.ccTotal
      )
    );
  }
  if (sigfig === undefined) {
    return total;
  }
  return ceilToDecimalPlaces(total, sigfig);
}

/**
 * Line sell for profit ($ and %). Merchandise and fee sell only.
 * Sales tax, the credit card fee, and the RCS admin fee are pass-throughs.
 * The card fee is figured on the admin fee, so it stays out of this sell too.
 */
export const getSellTotalForProfit = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT
): number => {
  const breakdown = getQuotePriceBreakdown(vendorQuote, {
    creditCardRatePercent,
  });
  return breakdown.subtotal;
};

/** % profit on cost (∞ when cost is 0 and there's profit). */
export const getProfitPercent = (sell: number, cost: number): number => {
  if (cost === 0) {
    return sell === 0 ? 0 : Infinity;
  }
  return ((sell - cost) / cost) * 100;
};

export interface QuoteMarkupFigures {
  /** Vendor base + vendor fees + RCS admin fee. The credit card fee is not included. */
  cost: number;
  /** Client total including the credit card fee, minus {@link cost}. Tax stays out. */
  profit: number;
  /** Profit ÷ cost, as a percent rounded to 4 decimal places. */
  markupPercent: number;
}

/**
 * Cost, profit, and markup for the pricing summary.
 * Cost = vendor base + shipping and other vendor fees + RCS admin fee.
 * The credit card fee is left out of cost and counted in profit.
 * Admin is the remainder after the card fee is rounded up, so
 * sell + admin + card still equals the client total.
 */
export const getQuoteMarkupFigures = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT,
  taxRatePercent?: number | null
): QuoteMarkupFigures => {
  const breakdown = getQuotePriceBreakdown(vendorQuote, {
    creditCardRatePercent,
    taxRatePercent,
  });
  const subtotalCents = roundCentsHalfUp(breakdown.subtotal);
  const beforeTaxGrand = roundCentsHalfUp(
    subtotalCents +
      roundCentsHalfUp(breakdown.adminTotal) +
      roundCentsHalfUp(breakdown.ccTotal)
  );
  let ccCents =
    breakdown.ccTotal > 0 ? ceilToDecimalPlaces(breakdown.ccTotal, 2) : 0;
  let adminCents = roundCentsHalfUp(beforeTaxGrand - subtotalCents - ccCents);
  if (!(breakdown.adminTotal > 0) || adminCents < 0) {
    adminCents = 0;
    ccCents = roundCentsHalfUp(Math.max(0, beforeTaxGrand - subtotalCents));
  }
  const cost = roundCentsHalfUp(
    getVendorTotalExcludingRcs(vendorQuote) + adminCents
  );
  const sell = roundCentsHalfUp(subtotalCents + adminCents + ccCents);
  const profit = roundCentsHalfUp(sell - cost);
  const rawPercent = getProfitPercent(sell, cost);
  return {
    cost,
    profit,
    markupPercent: Number.isFinite(rawPercent)
      ? roundToDecimalPlaces(rawPercent, 4)
      : rawPercent,
  };
};

/** Cost for markup. See {@link getQuoteMarkupFigures}. */
export const getMarkupCost = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT,
  taxRatePercent?: number | null
): number =>
  getQuoteMarkupFigures(vendorQuote, creditCardRatePercent, taxRatePercent)
    .cost;

/** Markup on cost, including the credit card fee in profit. See {@link getQuoteMarkupFigures}. */
export const getMarkupOnCostPercent = (
  vendorQuote: VendorQuote,
  creditCardRatePercent: number = DEFAULT_CREDIT_CARD_RATE_PERCENT,
  taxRatePercent?: number | null
): number =>
  getQuoteMarkupFigures(vendorQuote, creditCardRatePercent, taxRatePercent)
    .markupPercent;

/**
 * Client grand before credit card (for profit). Same inputs as getCustomerOrderTotal
 * but never adds CC.
 */
export function getCustomerOrderTotalBeforeCreditCard(
  vendorQuote: VendorQuote,
  includeItemizedFees = true,
  includeHiddenFees = true,
  sigfig?: number
): number {
  const total = getClientSellBeforeCreditCard(
    vendorQuote,
    includeItemizedFees,
    includeHiddenFees
  );
  if (arguments.length < 4) {
    return roundToDecimalPlaces(total, 2);
  }
  if (sigfig === undefined) {
    return total;
  }
  return roundToDecimalPlaces(total, sigfig);
}
